import { Injectable } from '@angular/core';

export const EVENT_START_RETRY_WARNING =
  'The previous Start could not be confirmed. The event is not live, but some webinar services may already have started. Trying again may repeat those operations. Try Start again?';

/** Keep active writes single; a disconnected Start requires an explicit recovery. */
@Injectable({ providedIn: 'root' })
export class EventStartAttemptService {
  private readonly pending = new Set<number>();
  private readonly active = new Set<number>();
  private readonly prefix = 'citizex:event-start-pending:v1:';

  isPending(eventId: number): boolean {
    try {
      return (
        this.pending.has(eventId) ||
        sessionStorage.getItem(this.prefix + eventId) === '1'
      );
    } catch {
      return this.pending.has(eventId);
    }
  }

  isInFlight(eventId: number): boolean {
    return this.active.has(eventId);
  }

  begin(eventId: number, retryConfirmed = false): boolean {
    if (
      this.isInFlight(eventId) ||
      (this.isPending(eventId) && !retryConfirmed)
    )
      return false;
    this.pending.add(eventId);
    this.active.add(eventId);
    try {
      sessionStorage.setItem(this.prefix + eventId, '1');
    } catch {
      /* The in-memory guard still protects this session. */
    }
    return true;
  }

  settle(eventId: number): void {
    this.active.delete(eventId);
    this.pending.delete(eventId);
    try {
      sessionStorage.removeItem(this.prefix + eventId);
    } catch {
      /* Storage may be unavailable. */
    }
  }

  observeLive(eventId: number): void {
    if (!this.isInFlight(eventId)) this.settle(eventId);
  }

  fail(eventId: number, error: any): void {
    this.active.delete(eventId);
    const reason =
      error?.error?.errorCode ||
      error?.error?.message ||
      error?.errorCode ||
      error?.message;
    if (
      [
        'EVENT_PROVIDER_NOT_READY',
        'EVENT_TIMEZONE_INVALID',
        'EVENT_SCHEDULE_INVALID',
        'EVENT_NOT_FOUND',
        'EVENT_UNAVAILABLE',
        'EVENT_ENDED',
        'AUTHENTICATION_REQUIRED',
      ].includes(reason)
    )
      this.settle(eventId);
    // A transport/gateway failure does not establish whether the server stopped.
    // The persisted marker remains recoverable through a fresh read + confirmation.
  }
}
